import {
  confirmedReceiptConfirmationSourceTerm,
  paymentMethodTerm,
  paymentStatusTerm,
} from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  confirmedReceiptBusinessMetricDetailQueryKey,
  getConfirmedReceiptBusinessMetricDetail,
} from "./businessMetricDictionary";

export type ConfirmedReceiptBusinessMetricDetailRoute =
  | { kind: "NONE" }
  | { kind: "INVALID" }
  | { kind: "DETAIL"; businessDate: string };

type ConfirmedReceiptDetailLoader = typeof getConfirmedReceiptBusinessMetricDetail;

type ConfirmedReceiptBusinessMetricDetailPanelProps = {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  route: Exclude<ConfirmedReceiptBusinessMetricDetailRoute, { kind: "NONE" }>;
  loader?: ConfirmedReceiptDetailLoader;
};

function isCanonicalBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

export function confirmedReceiptBusinessMetricDetailRouteFromSearch(
  search: string,
): ConfirmedReceiptBusinessMetricDetailRoute {
  const params = new URLSearchParams(search);
  const views = params.getAll("view");
  if (views.length === 0) return { kind: "NONE" };

  const businessDates = params.getAll("businessDate");
  if (
    [...params.entries()].length !== 2 ||
    views.length !== 1 ||
    views[0] !== "CONFIRMED_RECEIPT_DETAILS" ||
    businessDates.length !== 1
  ) {
    return { kind: "INVALID" };
  }
  const businessDate = businessDates[0] ?? "";
  return isCanonicalBusinessDate(businessDate)
    ? { kind: "DETAIL", businessDate }
    : { kind: "INVALID" };
}

function formatMoney(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function formatTime(timestamp: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone,
  }).format(new Date(timestamp));
}

function AuthorizedConfirmedReceiptBusinessMetricDetail({
  operator,
  route,
  loader,
}: {
  operator: OperatorIdentity;
  route: Extract<ConfirmedReceiptBusinessMetricDetailRoute, { kind: "DETAIL" }>;
  loader: ConfirmedReceiptDetailLoader;
}): React.JSX.Element {
  const context = {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
  };
  const detail = useQuery({
    queryKey: confirmedReceiptBusinessMetricDetailQueryKey(context, route.businessDate),
    queryFn: ({ signal }) => loader(route.businessDate, context, signal),
    refetchOnMount: "always",
    retry: false,
  });
  const responseMatchesSelection =
    detail.data?.tenantId === operator.tenantId &&
    detail.data.storeId === operator.storeId &&
    detail.data.businessDate === route.businessDate;
  const visibleDetail =
    !detail.isFetching && !detail.isError && responseMatchesSelection ? detail.data : undefined;
  const responseUnsafe =
    !detail.isFetching && detail.data !== undefined && !responseMatchesSelection;

  return (
    <main className="commercial-desk" aria-label="实际付款营业日实收完整明细">
      <header className="commerce-header">
        <div>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
          <small>{route.businessDate} · 完整确认实收账本</small>
          <h1>营业日确认实收明细</h1>
          <p>
            实收按款项实际到账或门店登记确认的营业日归属。退款另行列示，不会从这里的原始实收中静默抵减；请结合退款明细查看当前留存。
          </p>
        </div>
      </header>

      {detail.isFetching ? (
        <output className="empty-state" aria-live="polite">
          正在核对 {route.businessDate}{" "}
          的完整确认实收账本，确认前不会显示旧日期、旧账号或旧门店的金额。
        </output>
      ) : detail.isError || responseUnsafe ? (
        <div className="error-band" role="alert">
          <span>
            这项确认实收明细暂时无法安全读取，旧明细和金额已隐藏；只影响本项查看，不表示整套 ERP
            离线。
          </span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => detail.refetch()}
          >
            重新读取本项实收
          </button>
        </div>
      ) : visibleDetail ? (
        <>
          <section className="summary-grid" aria-label="营业日确认实收汇总">
            <div>
              <span>原始实收合计</span>
              <strong>{formatMoney(visibleDetail.valueFen)}</strong>
            </div>
            <div>
              <span>确认实收条数</span>
              <strong>{visibleDetail.factCount} 条</strong>
            </div>
            <div>
              <span>最近生成时间</span>
              <strong>
                <time dateTime={visibleDetail.generatedAt}>
                  {formatTime(visibleDetail.generatedAt, visibleDetail.timeZone)}
                </time>
              </strong>
            </div>
          </section>

          {visibleDetail.items.length === 0 ? (
            <section className="empty-state" aria-label="营业日确认实收为空">
              <strong>该营业日没有确认实收记录</strong>
              <p>
                完整确认实收账本已核对；这里只表示本项为零，不代表订单、退款或整套系统没有记录。
              </p>
            </section>
          ) : (
            <section aria-label="营业日逐笔确认实收">
              <div className="commerce-section-title">
                <div>
                  <h2>逐笔确认实收</h2>
                  <p>以下记录按实际到账或登记确认时间展示，不在页面重新计算资金。</p>
                </div>
              </div>
              <div className="member-lifecycle-timeline-list">
                {visibleDetail.items.map((item) => (
                  <article key={item.receiptNumber}>
                    <div>
                      <strong>实收号 {item.receiptNumber}</strong>
                      <span>
                        确认时间：
                        <time dateTime={item.recordedAt}>
                          {formatTime(item.recordedAt, visibleDetail.timeZone)}
                        </time>
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>原订单</dt>
                        <dd>
                          <Link
                            className="member-lifecycle-order-link"
                            to={`/orders?orderQuery=${encodeURIComponent(item.orderNumber)}`}
                          >
                            {item.orderNumber}
                          </Link>
                        </dd>
                      </div>
                      <div>
                        <dt>原订单营业日</dt>
                        <dd>{item.orderBusinessDate}</dd>
                      </div>
                      <div>
                        <dt>桌号</dt>
                        <dd>{item.tableNumber}</dd>
                      </div>
                      <div>
                        <dt>付款方式</dt>
                        <dd>{paymentMethodTerm(item.paymentMethod).label}</dd>
                      </div>
                      <div>
                        <dt>付款状态</dt>
                        <dd>{paymentStatusTerm(item.paymentStatus).label}</dd>
                      </div>
                      <div>
                        <dt>原始实收</dt>
                        <dd>{formatMoney(item.receivedAmountFen)}</dd>
                      </div>
                      <div>
                        <dt>全生命周期累计退款</dt>
                        <dd>{formatMoney(item.lifetimeRefundedFen)}</dd>
                      </div>
                      <div>
                        <dt>当前留存</dt>
                        <dd>{formatMoney(item.retainedAmountFen)}</dd>
                      </div>
                      <div>
                        <dt>确认来源</dt>
                        <dd>
                          {confirmedReceiptConfirmationSourceTerm(item.confirmationSource).label}
                        </dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      ) : null}
    </main>
  );
}

export function ConfirmedReceiptBusinessMetricDetailPanel({
  operator,
  effectiveRoles,
  route,
  loader = getConfirmedReceiptBusinessMetricDetail,
}: ConfirmedReceiptBusinessMetricDetailPanelProps): React.JSX.Element {
  if (route.kind === "INVALID") {
    return (
      <main className="commercial-desk" aria-label="确认实收明细链接无效">
        <div className="error-band" role="alert">
          <span>确认实收明细的营业日期无法安全确认，系统没有读取或展示普通订单列表。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  const authorized = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  if (!authorized) {
    return (
      <main className="commercial-desk" aria-label="确认实收明细无查看权限">
        <div className="error-band" role="alert">
          <span>当前账号不能查看全店确认实收明细，系统没有读取普通订单或实收金额。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <AuthorizedConfirmedReceiptBusinessMetricDetail
      key={`${operator.tenantId}\u0000${operator.storeId}\u0000${operator.id}\u0000${route.businessDate}`}
      operator={operator}
      route={route}
      loader={loader}
    />
  );
}
